/**
 * FDI tooth chart geometry.
 *
 * The chart is drawn from data, not hand-placed, so adding primary dentition or
 * changing spacing is a number change rather than an SVG rewrite.
 *
 * Layout follows how a clinician looks at a seated patient: the patient's RIGHT
 * is on the viewer's LEFT. That is why quadrant 1 (upper right) is drawn first,
 * left-to-right, and quadrant 2 (upper left) continues to its right. Getting
 * this backwards is the classic dental-chart bug and it mislabels every tooth.
 */

export const PERMANENT_ROWS = [
  { quadrants: [1, 2], arch: 'UPPER' },
  { quadrants: [4, 3], arch: 'LOWER' },
];

export const PRIMARY_ROWS = [
  { quadrants: [5, 6], arch: 'UPPER' },
  { quadrants: [8, 7], arch: 'LOWER' },
];

export const TOOTH_W = 26;
export const TOOTH_H = 34;
export const GAP = 3;
export const MIDLINE_GAP = 12;
export const ROW_GAP = 26;

/** Positions per quadrant: 8 permanent, 5 primary. */
const countFor = (quadrant) => (quadrant <= 4 ? 8 : 5);

/**
 * Builds [{ fdi, x, y, quadrant, position, arch, side }] for one dentition.
 * A quadrant on the viewer's left is drawn outward-to-midline, so the molars
 * sit at the outside of the chart exactly as they do in the mouth.
 */
export function buildChart(dentition = 'PERMANENT') {
  const rows = dentition === 'PERMANENT' ? PERMANENT_ROWS : PRIMARY_ROWS;
  const perQuadrant = countFor(rows[0].quadrants[0]);
  const teeth = [];

  rows.forEach((row, rowIndex) => {
    const y = rowIndex * (TOOTH_H + ROW_GAP);

    row.quadrants.forEach((quadrant, side) => {
      for (let i = 0; i < perQuadrant; i++) {
        // Left half of the chart counts down to the midline; right half counts up.
        const position = side === 0 ? perQuadrant - i : i + 1;
        const indexInRow = side === 0 ? i : perQuadrant + i;
        const x = indexInRow * (TOOTH_W + GAP) + (side === 1 ? MIDLINE_GAP : 0);

        teeth.push({
          fdi: `${quadrant}${position}`,
          quadrant,
          position,
          arch: row.arch,
          side: side === 0 ? 'RIGHT' : 'LEFT',
          x,
          y,
        });
      }
    });
  });

  return teeth;
}

export function chartSize(dentition = 'PERMANENT') {
  const perQuadrant = dentition === 'PERMANENT' ? 8 : 5;
  return {
    width: perQuadrant * 2 * (TOOTH_W + GAP) + MIDLINE_GAP,
    height: 2 * TOOTH_H + ROW_GAP,
  };
}


/**
 * Tooth type by position. Anatomy, and the reason the chart is not a row of
 * identical boxes: an incisor, a canine, a premolar and a molar look nothing
 * alike, and a clinician reads the chart by SHAPE long before reading numbers.
 */
export function toothKind(position) {
  if (position <= 2) return 'INCISOR';
  if (position === 3) return 'CANINE';
  if (position <= 5) return 'PREMOLAR';
  return 'MOLAR';
}

/** Crown width per kind, as a fraction of the slot. Molars really are wider. */
const KIND_WIDTH = { INCISOR: 0.74, CANINE: 0.78, PREMOLAR: 0.86, MOLAR: 1 };

/**
 * Where a tooth's biting edge lands on screen.
 *
 * Teeth in opposing arches point AT each other: an upper tooth bites downward,
 * a lower tooth bites upward. Draw both the same way and the chart reads as two
 * rows of the same jaw — which is what it did until someone looked at it.
 */
export function bitesDownward(tooth) {
  return tooth.arch === 'UPPER';
}

/**
 * The crown outline for one tooth, in its own 0..TOOTH_W / 0..TOOTH_H box.
 *
 * The paths below are authored BITING EDGE FIRST: every cusp, notch and chisel
 * edge sits at a low `v`, and the root end at a high one. `y()` is what decides
 * which way up that ends on screen.
 */
export function crownPath(tooth) {
  const kind = toothKind(tooth.position);
  const w = TOOTH_W * KIND_WIDTH[kind];
  const x0 = (TOOTH_W - w) / 2;
  const x1 = x0 + w;
  const h = TOOTH_H;

  // Low `v` is the biting edge. An upper tooth bites DOWN, so low `v` maps to a
  // large screen y; a lower tooth bites UP, so it maps to a small one.
  const y = (v) => (bitesDownward(tooth) ? h - v : v);

  switch (kind) {
    case 'INCISOR':
      // A flat chisel edge, tapering slightly toward the root.
      return [
        `M${x0 + 2},${y(0.1 * h)}`,
        `C${x0 - 1},${y(0.45 * h)} ${x0},${y(0.8 * h)} ${x0 + 1.5},${y(0.95 * h)}`,
        `L${x1 - 1.5},${y(0.95 * h)}`,
        `C${x1},${y(0.8 * h)} ${x1 + 1},${y(0.45 * h)} ${x1 - 2},${y(0.1 * h)}`,
        'Z',
      ].join(' ');

    case 'CANINE':
      // One pointed cusp — the only tooth with a single apex.
      return [
        `M${(x0 + x1) / 2},${y(0.02 * h)}`,
        `C${x0 + 1},${y(0.3 * h)} ${x0 - 1},${y(0.6 * h)} ${x0 + 1.5},${y(0.95 * h)}`,
        `L${x1 - 1.5},${y(0.95 * h)}`,
        `C${x1 + 1},${y(0.6 * h)} ${x1 - 1},${y(0.3 * h)} ${(x0 + x1) / 2},${y(0.02 * h)}`,
        'Z',
      ].join(' ');

    case 'PREMOLAR':
      // Two cusps: a shallow notch in the biting edge.
      return [
        `M${x0 + 1},${y(0.12 * h)}`,
        `Q${(x0 + x1) / 2},${y(0.26 * h)} ${x1 - 1},${y(0.12 * h)}`,
        `C${x1 + 1},${y(0.5 * h)} ${x1},${y(0.82 * h)} ${x1 - 2},${y(0.96 * h)}`,
        `L${x0 + 2},${y(0.96 * h)}`,
        `C${x0},${y(0.82 * h)} ${x0 - 1},${y(0.5 * h)} ${x0 + 1},${y(0.12 * h)}`,
        'Z',
      ].join(' ');

    default:
      // Molar: a broad crown with a scalloped, multi-cusp table.
      return [
        `M${x0 + 1},${y(0.14 * h)}`,
        `Q${x0 + w * 0.25},${y(0.02 * h)} ${x0 + w * 0.5},${y(0.14 * h)}`,
        `Q${x0 + w * 0.75},${y(0.02 * h)} ${x1 - 1},${y(0.14 * h)}`,
        `C${x1 + 1},${y(0.5 * h)} ${x1},${y(0.84 * h)} ${x1 - 2.5},${y(0.97 * h)}`,
        `L${x0 + 2.5},${y(0.97 * h)}`,
        `C${x0},${y(0.84 * h)} ${x0 - 1},${y(0.5 * h)} ${x0 + 1},${y(0.14 * h)}`,
        'Z',
      ].join(' ');
  }
}

/**
 * How much of the tooth box is left uncovered by surfaces, on every edge.
 *
 * This rim is not decoration — it is the tooth's own hit area. The five surface
 * regions of a molar tile their box exactly (M+O+D across the middle, B and L
 * across the full width), so without an inset a SELECTED molar has no clickable
 * tooth left at all: every pixel belongs to a surface, and clicking the tooth to
 * deselect it silently does nothing. The keyboard path kept working, so the
 * mouse and the keyboard disagreed about whether the control was a toggle.
 */
const SURFACE_INSET = 0.16;

/** Maps a rect in unit tooth-space into the inset box, preserving proportions. */
function inset({ x, y, w, h }) {
  const span = 1 - 2 * SURFACE_INSET;
  return {
    x: SURFACE_INSET + x * span,
    y: SURFACE_INSET + y * span,
    w: w * span,
    h: h * span,
  };
}

/**
 * Surface sub-regions within a tooth box, as fractions of its width and height.
 * Mesial and distal swap sides depending on which half of the mouth the tooth
 * is in — mesial always faces the midline, which is the whole point of the
 * word, so it cannot be a fixed left or right.
 */
export function surfaceRects(tooth) {
  const towardMidlineIsRight = tooth.side === 'RIGHT';
  const mesialX = towardMidlineIsRight ? 0.66 : 0;
  const distalX = towardMidlineIsRight ? 0 : 0.66;

  // Buccal faces outward, lingual toward the tongue: for the upper arch drawn
  // above the midline, buccal is the top edge; for the lower arch it is the bottom.
  const buccalY = tooth.arch === 'UPPER' ? 0 : 0.72;
  const lingualY = tooth.arch === 'UPPER' ? 0.72 : 0;

  return {
    M: inset({ x: mesialX, y: 0.28, w: 0.34, h: 0.44 }),
    D: inset({ x: distalX, y: 0.28, w: 0.34, h: 0.44 }),
    B: inset({ x: 0, y: buccalY, w: 1, h: 0.28 }),
    L: inset({ x: 0, y: lingualY, w: 1, h: 0.28 }),
    O: inset({ x: 0.34, y: 0.28, w: 0.32, h: 0.44 }),
  };
}

/** Anterior teeth (positions 1–3) have no occlusal surface. */
export function surfacesForTooth(tooth) {
  return tooth.position <= 3 ? ['M', 'D', 'B', 'L'] : ['M', 'D', 'B', 'L', 'O'];
}
